//  External libs
import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPen, faLightbulb, faCode, faPowerOff } from "@fortawesome/free-solid-svg-icons";
import { Tooltip } from "react-tooltip";

// popupPage 
import DeviceEditPopupForm from "../components/popupPage/DeviceEditPopUp.js";
import DeviceUpdateFirmwarePopupForm from "../components/popupPage/DeviceFirmwarePopUp.js";


// mqtt services
import { flashLED, rebootDevice } from "../services/mqttService.js";

// utils
import { makeIdentifyToast } from "../utils/utils.js";

// Assets
import "./DialogComponent.css";

import { useDevice, useDeviceList } from "../hook/userDevice.js"

/**
 * A dialog component that displays a set of buttons and handles user interactions.
 *
 * @param {string} deviceId - the device id of the device to be controlled
 *
 * @returns {JSX.Element} The rendered dialog component.
 *
 * @example
 * <DeviceControlPanel
 *   deviceId={deviceId}
 * />
 */
const DeviceControlPanel = ({
  deviceId,
}) => {

  const { data: device, isLoading, error } = useDevice(deviceId);
  const { data: deviceTypes } = useDeviceList();
  const [online, setOnline] = useState(false);

  // Popup Form usage
  const [openPopup, setOpenPopup] = useState(null);
  const closePopup = () => setOpenPopup(null);
  const [popUpData, setPopUpData] = useState({});
  
  useEffect(() => {

    if (device?.status == "Online")
      setOnline(true);
    else
      setOnline(false);
  
  }, [device]);

  // Handle form submission
  const handleFormSubmit = (formData) => {};  

  // This function used for Add/Edit Device popup form
  const handleDeviceEditSelection = (devices, title, submitType) => {
    let device;
    if (devices == null) {
      device = null;
    } else {
      device = devices[0];
    }
    setPopUpData({
      device: device,
      title: title,
      submitType: submitType,
    });
    // Open the popup for adding or editing a device
    // submitType is used for differentiation (ToDo: why use submitType?)
    setOpenPopup("deviceAddOrEdit");
  };

    const handleDeviceFirmwareUpdateSelection = (devices) => {
    const device = devices[0];
    if (!device || !device.name || !device.deviceType || !device.firmware) {
      console.error("Invalid device object:", device);
      return;
    }
    if (!device || typeof device !== "object") {
      console.error("Invalid device object:", device);
      return;
    }
    setOpenPopup("firmwareUpdate");
  };


  const onButtonClick = (text, device) => {
    if (text === "edit") {
      handleDeviceEditSelection([device], "Edit Device", "update");
    } else if (text === "identify") {
        flashLED(device.id);
        makeIdentifyToast(device.id, "LED will flash for 15 seconds", faLightbulb);
    } else if (text === "firmware") {
      handleDeviceFirmwareUpdateSelection([device]);
    }
    else if (text === "reboot") {

        rebootDevice(device.id);
        makeIdentifyToast(device.id, "device will reboot", faPowerOff);
    }
  };  

  return (
    <body>
      <div>
          {/* popup box display control */}
          <div>
                {openPopup === "deviceAddOrEdit" && (
                  <DeviceEditPopupForm
                    onClose={closePopup}
                    deviceTypes={deviceTypes}
                    onSubmit={handleFormSubmit}
                    inputData={popUpData}
                  />
                )}
                {openPopup === "firmwareUpdate" && (
                  <DeviceUpdateFirmwarePopupForm
                    onClose={closePopup}
                    onSubmit={handleFormSubmit}
                    devices={[device]}
                  />
                )}
        </div>
      </div> 
      {/* edit button */}
      <button
        className="dialog-btn top-btn"
        id={`edit_btn_${deviceId}`}
        onClick={() => onButtonClick("edit", device)}
      >
        <span className="dialog-icon">
          <FontAwesomeIcon icon={faPen} size="xs" />
        </span>{" "}
        Edit Device Configuration
      </button>
      <div className="dialog_tooltip">
        <Tooltip anchorSelect={`#edit_btn_${deviceId}`} place="left">
          Edit device configuration
        </Tooltip>
      </div>

      {/* identify button */}
      <button
        className="dialog-btn"
        id={`identify_btn_${deviceId}`}
        disabled={!online}
        onClick={() => onButtonClick("identify", device)}
      >
        <span className="dialog-icon">
          <FontAwesomeIcon icon={faLightbulb} size="xs" />
        </span>{" "}
        Identify the device
      </button>
      <div className="dialog_tooltip">
        <Tooltip anchorSelect={`#identify_btn_${deviceId}`} place="left">
          Device LED will flash for 15 seconds
        </Tooltip>
      </div>

      {/* firmware button */}
      <button
        className="dialog-btn"
        id={`fw_btn_${deviceId}`}
        disabled={!online}
        onClick={() => onButtonClick("firmware", device)}
      >
        <span className="dialog-icon">
          <FontAwesomeIcon icon={faCode} size="xs" />
        </span>{" "}
        Firmware Update
      </button>
      <div className="dialog_tooltip">
        <Tooltip anchorSelect={`#fw_btn_${deviceId}`} place="left">
          Update device firmware
        </Tooltip>
      </div>

      {/* reboot button */}
      <button
        className="dialog-btn"
        id={`reboot_btn_${deviceId}`}
        disabled={!online}
        onClick={() => onButtonClick("reboot", device)}
      >
        <span className="dialog-icon">
          <FontAwesomeIcon icon={faPowerOff} size="xs" />
        </span>{" "}
        Reboot
      </button>
      <div className="dialog_tooltip">
        <Tooltip anchorSelect={`#reboot_btn_${deviceId}`} place="left">
          Reboot the device
        </Tooltip>
      </div>

    </body>
  );
};

DeviceControlPanel.propTypes = {
  deviceId: PropTypes.string.isRequired
};

export default DeviceControlPanel;
